import { type ReactNode } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";

/**
 * ScrollReveal — race-cadence reveal-on-scroll wrapper.
 *
 * Wraps any block in a `motion.div` that fades + slides up the first time it
 * enters the viewport (framer-motion `whileInView` under the hood).
 *
 * For lists, wrap children in `<ScrollReveal.Group>` and replace each child's
 * outer element with `<ScrollReveal.Item>` so they cascade with a stagger.
 *
 * Honours `prefers-reduced-motion`: when set, renders children at the final
 * position with no transition. All wrappers are `<div>` — if the consumer
 * needs to preserve a semantic landmark (`<section>` etc.), the consumer
 * should keep that landmark outside of ScrollReveal and place ScrollReveal
 * inside it.
 */

const EASE_OUT_EXPO: [number, number, number, number] = [0.22, 1, 0.36, 1];
const VIEWPORT = { once: true, margin: "-80px" } as const;

type CommonProps = {
  children: ReactNode;
  className?: string;
  /** Vertical offset before reveal. Default 24px. */
  y?: number;
  /** Delay in seconds before this element runs. Ignored inside Group. */
  delay?: number;
};

/**
 * Single reveal — fade + slide up the first time it scrolls into view.
 */
function ScrollRevealBase({ children, className, y = 24, delay = 0 }: CommonProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration: 0.55, ease: EASE_OUT_EXPO, delay }}
    >
      {children}
    </motion.div>
  );
}

type GroupProps = {
  children: ReactNode;
  className?: string;
  /** Stagger between children, seconds. Capped at 0.08 by convention. */
  stagger?: number;
};

/**
 * Cascading group — wrap a list and replace each child's outermost JSX with
 * `<ScrollReveal.Item>` so they each consume the parent's variants and arrive
 * one after another like runners crossing a line.
 */
function ScrollRevealGroup({ children, className, stagger = 0.08 }: GroupProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: Math.min(stagger, 0.08) }
    }
  };

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={containerVariants}
    >
      {children}
    </motion.div>
  );
}

type ItemProps = Omit<CommonProps, "delay">;

/**
 * Item — consumes the parent ScrollRevealGroup's variants. Don't use without
 * a parent group; use `<ScrollReveal>` for standalone reveals.
 */
function ScrollRevealItem({ children, className, y = 24 }: ItemProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: EASE_OUT_EXPO }
    }
  };

  return (
    <motion.div className={className} variants={itemVariants}>
      {children}
    </motion.div>
  );
}

type ScrollRevealComponent = typeof ScrollRevealBase & {
  Group: typeof ScrollRevealGroup;
  Item: typeof ScrollRevealItem;
};

const ScrollReveal = ScrollRevealBase as ScrollRevealComponent;
ScrollReveal.Group = ScrollRevealGroup;
ScrollReveal.Item = ScrollRevealItem;

export { ScrollReveal, ScrollRevealGroup, ScrollRevealItem };
